import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import {
  BarChart3, Clock, Users, XCircle, CheckCircle, TrendingUp, Download,
  Search, Filter, Calendar, ChevronDown, ChevronUp, ArrowUpDown, Loader2,
  FileText, AlertCircle
} from 'lucide-react';

const TeacherReports = () => {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subjects, setSubjects] = useState([]);
  
  // Filters
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Sorting
  const [sortField, setSortField] = useState('total_classes');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Expanded rows for details
  const [expandedRows, setExpandedRows] = useState(new Set());

  useEffect(() => {
    fetchSubjects();
    fetchReport();
  }, []);

  const fetchSubjects = async () => {
    try {
      const response = await api.getSubjects();
      setSubjects(response.data);
    } catch (error) {
      console.error('Error fetching subjects:', error);
    }
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (selectedSubject) params.subject = selectedSubject;
      
      const response = await api.getTeachersPerformanceReport(params);
      setReportData(response.data);
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error('Failed to load performance report');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = () => {
    fetchReport();
  };

  const handleClearFilters = () => {
    setStartDate('');
    setEndDate('');
    setSelectedSubject('');
    setSearchQuery('');
    // Fetch without filters
    setTimeout(() => fetchReport(), 100);
  };

  const toggleRowExpanded = (teacherId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(teacherId)) {
      newExpanded.delete(teacherId);
    } else {
      newExpanded.add(teacherId);
    }
    setExpandedRows(newExpanded);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const exportTeacherClasses = async (teacher) => {
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (selectedSubject) params.subject = selectedSubject;
      const res = await api.exportTeacherClasses(teacher.teacher_id, params);
      const data = res.data;
      
      const headers = ['Date', 'Title', 'Subject', 'Topic', 'Type', 'Status', 'Planned Min', 'Actual Min', 'Counted Hours', 'Cancellation Reason', 'Substitute'];
      const rows = data.classes.map(c => [
        c.date, c.title, c.subject, c.topic || '', c.type, c.status,
        c.planned_minutes, c.actual_minutes, c.counted_hours,
        c.cancellation_reason || '', c.substitute || ''
      ]);
      // Add summary row
      rows.push([]);
      rows.push(['SUMMARY', '', '', '', '', '', '', '', '', '', '']);
      rows.push(['Total Classes', data.total_classes, '', '', '', '', '', '', data.total_counted_hours, '', '']);
      rows.push(['App Hours', '', '', '', '', '', '', '', data.app_hours, '', '']);
      rows.push(['YouTube Hours', '', '', '', '', '', '', '', data.youtube_hours, '', '']);
      rows.push(['Offline Hours', '', '', '', '', '', '', '', data.offline_hours, '', '']);

      const csvContent = [
        headers.map(h => `"${h}"`).join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${teacher.teacher_name.replace(/\s+/g, '_')}_classes_${startDate || 'all'}_to_${endDate || 'all'}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${data.total_classes} classes for ${teacher.teacher_name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to export');
    }
  };

  const exportToCSV = () => {
    if (!reportData?.teachers?.length) return;
    
    const headers = [
      'Teacher Name', 'Email', 'Phone', 'Total Classes', 'App Classes', 'YouTube Classes', 'Offline Classes',
      'Completed', 'New Recording', 'Old Recording', 'Cancelled', 'Proxy Used', 'Scheduled', 
      'Planned Hours', 'Actual Hours', 'App Hours', 'YouTube Hours', 'Offline Hours', 'Completion Rate (%)'
    ];
    
    const rows = reportData.teachers.map(t => [
      t.teacher_name,
      t.email,
      t.phone || '',
      t.total_classes,
      t.app_classes || 0,
      t.youtube_classes || 0,
      t.offline_classes || 0,
      t.completed_classes,
      t.new_recording_classes || 0,
      t.old_recording_classes || 0,
      t.cancelled_classes,
      t.proxy_classes,
      t.scheduled_classes,
      t.planned_hours,
      t.actual_hours,
      t.app_hours || 0,
      t.youtube_hours || 0,
      t.offline_hours || 0,
      t.completion_rate
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `teacher_performance_report_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('Report exported successfully');
  };

  // Filter and sort teachers
  const getFilteredAndSortedTeachers = () => {
    if (!reportData?.teachers) return [];
    
    let filtered = reportData.teachers.filter(t => 
      t.teacher_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.email.toLowerCase().includes(searchQuery.toLowerCase())
    );
    
    // Sort
    filtered.sort((a, b) => {
      const aVal = a[sortField] || 0;
      const bVal = b[sortField] || 0;
      if (sortOrder === 'asc') return aVal - bVal;
      return bVal - aVal;
    });
    
    return filtered;
  };

  const teachers = getFilteredAndSortedTeachers();

  const SortHeader = ({ field, label }) => (
    <th 
      className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100 select-none"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1">
        {label}
        <ArrowUpDown size={14} className={sortField === field ? 'text-purple-600' : 'text-slate-400'} />
      </div>
    </th>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div data-testid="teacher-reports-page" className="max-w-[1600px] mx-auto space-y-4 sm:space-y-6 px-2 sm:px-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-headings text-slate-900">Teacher Performance Report</h1>
          <p className="text-sm sm:text-base text-slate-600 font-body mt-1">
            Comprehensive analysis of teacher performance metrics
          </p>
        </div>
        <button
          onClick={exportToCSV}
          disabled={!reportData?.teachers?.length}
          className="w-full sm:w-auto px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
          data-testid="export-report-btn"
        >
          <Download size={18} />
          Export CSV
        </button>
      </div>

      {/* Summary Stats */}
      {reportData?.summary && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-purple-100 rounded-lg">
                <Users className="text-purple-600" size={24} />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{reportData.summary.total_teachers}</p>
                <p className="text-sm text-slate-500">Total Teachers</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-green-100 rounded-lg">
                <CheckCircle className="text-green-600" size={24} />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{reportData.summary.total_classes_completed}</p>
                <p className="text-sm text-slate-500">Classes Completed</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-red-100 rounded-lg">
                <XCircle className="text-red-600" size={24} />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{reportData.summary.total_classes_cancelled}</p>
                <p className="text-sm text-slate-500">Classes Cancelled</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Clock className="text-blue-600" size={24} />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{reportData.summary.total_hours_taught}</p>
                <p className="text-sm text-slate-500">Total Hours Taught</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-amber-100 rounded-lg">
                <TrendingUp className="text-amber-600" size={24} />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{reportData.summary.average_completion_rate}%</p>
                <p className="text-sm text-slate-500">Avg Completion Rate</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex items-center gap-2 mb-4">
          <Filter size={18} className="text-slate-500" />
          <span className="font-medium text-slate-700">Filters</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              data-testid="filter-start-date"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              data-testid="filter-end-date"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Subject</label>
            <select
              value={selectedSubject}
              onChange={(e) => setSelectedSubject(e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              data-testid="filter-subject"
            >
              <option value="">All Subjects</option>
              {subjects.map(s => (
                <option key={s.id} value={s.name}>{s.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Search Teacher</label>
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                data-testid="filter-search"
              />
            </div>
          </div>
          <div className="flex items-end gap-2 sm:col-span-2 lg:col-span-1">
            <button
              onClick={handleApplyFilters}
              className="flex-1 sm:flex-none px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-medium transition-colors"
              data-testid="apply-filters-btn"
            >
              Apply
            </button>
            <button
              onClick={handleClearFilters}
              className="flex-1 sm:flex-none px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 text-sm font-medium transition-colors"
              data-testid="clear-filters-btn"
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Report Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider w-8"></th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Teacher</th>
                <SortHeader field="total_classes" label="Total" />
                <SortHeader field="completed_classes" label="Completed" />
                <SortHeader field="new_recording_classes" label="New Rec" />
                <SortHeader field="old_recording_classes" label="Old Rec" />
                <SortHeader field="cancelled_classes" label="Cancelled" />
                <SortHeader field="proxy_classes" label="Proxy" />
                <SortHeader field="actual_hours" label="Total Hours" />
                <SortHeader field="app_hours" label="App Hrs" />
                <SortHeader field="youtube_hours" label="YT Hrs" />
                <SortHeader field="offline_hours" label="Offline Hrs" />
                <SortHeader field="completion_rate" label="Completion %" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {teachers.length === 0 ? (
                <tr>
                  <td colSpan="12" className="px-4 py-12 text-center text-slate-500">
                    <BarChart3 size={48} className="mx-auto mb-3 text-slate-300" />
                    <p className="font-medium">No data available</p>
                    <p className="text-sm mt-1">Try adjusting your filters</p>
                  </td>
                </tr>
              ) : (
                teachers.map((teacher) => (
                  <React.Fragment key={teacher.teacher_id}>
                    <tr 
                      className="hover:bg-slate-50 cursor-pointer"
                      onClick={() => toggleRowExpanded(teacher.teacher_id)}
                      data-testid={`teacher-row-${teacher.teacher_id}`}
                    >
                      <td className="px-4 py-3">
                        {expandedRows.has(teacher.teacher_id) ? 
                          <ChevronUp size={16} className="text-slate-400" /> : 
                          <ChevronDown size={16} className="text-slate-400" />
                        }
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-slate-900">{teacher.teacher_name}</p>
                          <p className="text-xs text-slate-500">{teacher.email}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-700 font-medium">{teacher.total_classes}</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1 text-green-700 bg-green-50 px-2 py-1 rounded-md text-sm font-medium">
                          <CheckCircle size={14} />
                          {teacher.completed_classes}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-sm font-medium ${
                          teacher.new_recording_classes > 0 ? 'text-purple-700 bg-purple-50' : 'text-slate-500 bg-slate-50'
                        }`}>
                          {teacher.new_recording_classes || 0}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-sm font-medium ${
                          teacher.old_recording_classes > 0 ? 'text-slate-700 bg-slate-100' : 'text-slate-500 bg-slate-50'
                        }`}>
                          {teacher.old_recording_classes || 0}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-sm font-medium ${
                          teacher.cancelled_classes > 0 ? 'text-red-700 bg-red-50' : 'text-slate-500 bg-slate-50'
                        }`}>
                          <XCircle size={14} />
                          {teacher.cancelled_classes}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-sm font-medium ${
                          teacher.proxy_classes > 0 ? 'text-amber-700 bg-amber-50' : 'text-slate-500 bg-slate-50'
                        }`}>
                          {teacher.proxy_classes}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1 text-blue-700 bg-blue-50 px-2 py-1 rounded-md text-sm font-medium">
                          <Clock size={14} />
                          {teacher.actual_hours}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1 text-indigo-700 bg-indigo-50 px-2 py-1 rounded-md text-sm font-medium">
                          {teacher.app_hours || 0}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1 text-red-700 bg-red-50 px-2 py-1 rounded-md text-sm font-medium">
                          {teacher.youtube_hours || 0}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1 text-orange-700 bg-orange-50 px-2 py-1 rounded-md text-sm font-medium">
                          {teacher.offline_hours || 0}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div 
                              className={`h-full rounded-full ${
                                teacher.completion_rate >= 80 ? 'bg-green-500' :
                                teacher.completion_rate >= 60 ? 'bg-amber-500' : 'bg-red-500'
                              }`}
                              style={{ width: `${teacher.completion_rate}%` }}
                            />
                          </div>
                          <span className={`text-sm font-medium ${
                            teacher.completion_rate >= 80 ? 'text-green-600' :
                            teacher.completion_rate >= 60 ? 'text-amber-600' : 'text-red-600'
                          }`}>
                            {teacher.completion_rate}%
                          </span>
                        </div>
                      </td>
                    </tr>
                    
                    {/* Expanded Details Row */}
                    {expandedRows.has(teacher.teacher_id) && (
                      <tr className="bg-slate-50">
                        <td colSpan="10" className="px-6 py-4">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {/* Contact Info */}
                            <div>
                              <h4 className="text-sm font-semibold text-slate-700 mb-2">Contact Information</h4>
                              <div className="space-y-1 text-sm text-slate-600">
                                <p><span className="text-slate-400">Phone:</span> {teacher.phone || 'Not provided'}</p>
                                <p><span className="text-slate-400">Email:</span> {teacher.email}</p>
                              </div>
                            </div>
                            
                            {/* Subjects */}
                            <div>
                              <h4 className="text-sm font-semibold text-slate-700 mb-2">Subjects Taught</h4>
                              <div className="flex flex-wrap gap-1">
                                {teacher.subjects_taught?.length > 0 ? (
                                  teacher.subjects_taught.map((subj, idx) => (
                                    <span key={idx} className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-md text-xs">
                                      {subj}
                                    </span>
                                  ))
                                ) : (
                                  <span className="text-sm text-slate-400">No classes recorded</span>
                                )}
                              </div>
                            </div>
                            
                            {/* Cancellation Reasons */}
                            <div>
                              <h4 className="text-sm font-semibold text-slate-700 mb-2">
                                Cancellation Reasons ({teacher.cancellation_reasons?.length || 0})
                              </h4>
                              {teacher.cancellation_reasons?.length > 0 ? (
                                <ul className="space-y-1 text-sm text-slate-600 max-h-24 overflow-y-auto">
                                  {teacher.cancellation_reasons.slice(0, 5).map((reason, idx) => (
                                    <li key={idx} className="flex items-start gap-2">
                                      <AlertCircle size={14} className="text-red-400 mt-0.5 flex-shrink-0" />
                                      <span>{reason}</span>
                                    </li>
                                  ))}
                                  {teacher.cancellation_reasons.length > 5 && (
                                    <li className="text-slate-400 text-xs">
                                      +{teacher.cancellation_reasons.length - 5} more...
                                    </li>
                                  )}
                                </ul>
                              ) : (
                                <span className="text-sm text-green-600">No cancellations</span>
                              )}
                            </div>
                          </div>
                          
                          {/* Hours Breakdown */}
                          <div className="mt-4 pt-4 border-t border-slate-200">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-6 text-sm">
                                <div>
                                  <span className="text-slate-400">Planned Hours:</span>
                                  <span className="ml-2 font-medium text-slate-700">{teacher.planned_hours} hrs</span>
                                </div>
                                <div>
                                  <span className="text-slate-400">Actual Hours:</span>
                                  <span className="ml-2 font-medium text-slate-700">{teacher.actual_hours} hrs</span>
                                </div>
                                <div>
                                  <span className="text-slate-400">Scheduled (Upcoming):</span>
                                  <span className="ml-2 font-medium text-slate-700">{teacher.scheduled_classes}</span>
                                </div>
                              </div>
                              <button
                                onClick={() => exportTeacherClasses(teacher)}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-lg text-xs font-medium hover:bg-indigo-100 transition"
                                data-testid={`export-classes-${teacher.teacher_id}`}
                              >
                                <Download size={14} /> Export All Classes
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Footer */}
        {teachers.length > 0 && (
          <div className="px-4 py-3 bg-slate-50 border-t border-slate-200 text-sm text-slate-500">
            Showing {teachers.length} teachers
            {(startDate || endDate || selectedSubject) && (
              <span className="ml-2 text-purple-600">
                (filtered)
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TeacherReports;
